import { useEffect, useRef } from "react";

/**
 * Similar to useEffect, but it will only run the callback passed as parameter when
 * the observed value actually changes, therefore it will never run on component mount or unmount.
 *
 * @param callback Callback to be executed. Receives the new value as well as the old value as parameters. If it returns a function, it will be used as the underlying useEffect's destructor.
 * @param observedValue Value to be observed for changes.
 */
export function useUpdatedValue<T>(
  callback: (newValue: T, oldValue: T) => ReturnType<React.EffectCallback>,
  observedValue: T,
): void {
  const oldValue = useRef<T>(observedValue);

  useEffect(() => {
    const oldStoredValue = oldValue.current;
    oldValue.current = observedValue;

    if (oldStoredValue !== observedValue) {
      return callback(observedValue, oldStoredValue);
    }
  }, [observedValue]);
}
